import { expandUrlParams, getEndpointTemplate } from '@bymason/utils';
import { XrayRtcStateMachine } from './state-machine';
import XrayRtcReporter from './reporter';
import {
  CLIENT_ACK,
  DEVICE_ACK,
  XrayRtcConnectionEvent,
  XrayRtcConnectionOptions,
  XrayRtcFeature,
} from './types';
import { log } from './utils';

const DEFAULT_SESSION_TIMEOUT_SECONDS = 15 * 60; // 15-minutes
const ICE_SERVERS = ['stun:stun.l.google.com:19302'];
const MIN_BEARER_TOKEN_LENGTH = 301;
const WEBSOCKET_NORMAL_EXIT_CODE = 1000;
const WEBSOCKET_TIMEOUT_EXIT_CODE = 4000;

export abstract class XrayRtcConnectionManager {
  public constructor(options: XrayRtcConnectionOptions) {
    this.options = options;
    this.webRtcNotSupported = !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia;
    this.stateMachine = new XrayRtcStateMachine();
    this.initStateMachine();
  }

  public start(params?: URLSearchParams) {
    if (this.webRtcNotSupported) {
      this.stateMachine.emit(XrayRtcConnectionEvent.connectionNotSupportedError);
      return;
    }

    try {
      this.closeConnections();
      this.stateMachine.reset();
      this.initWebSocket(params);

      this.stateMachine.emit(XrayRtcConnectionEvent.connectionStart);
    } catch (error) {
      log('failed to start', error as Error, 'error');
    }
  }

  public stop() {
    if (this.webRtcNotSupported) {
      this.stateMachine.emit(XrayRtcConnectionEvent.connectionNotSupportedError);
      return;
    }

    this.closeConnections();
    this.stateMachine.emit(XrayRtcConnectionEvent.connectionStop);
  }

  private dataChannel: RTCDataChannel | null = null;
  private options: XrayRtcConnectionOptions;
  private peerConnection: RTCPeerConnection | null = null;
  private reporter: XrayRtcReporter | null = null;
  private stateMachine: XrayRtcStateMachine;
  private socket: WebSocket | null = null;
  private webRtcNotSupported = false;

  private closeConnections(timeout = false) {
    // IMPORTANT: send close message to device to allow it to better handle the actual socket.close()
    this.socket?.send('client:close');
    this.reporter?.close();
    this.peerConnection?.close();
    this.socket?.close(timeout ? WEBSOCKET_TIMEOUT_EXIT_CODE : WEBSOCKET_NORMAL_EXIT_CODE);
  }

  // ---------------------------------------------------------
  //
  // initializers
  //
  // ---------------------------------------------------------

  private initDataChannel() {
    const dataChannel = this.peerConnection?.createDataChannel('client');

    if (dataChannel) {
      this.dataChannel = dataChannel;

      dataChannel.onclose = () => {
        this.stateMachine.emit(XrayRtcConnectionEvent.clientDataChannelClosed);
      };

      dataChannel.onerror = (evt) => {
        log('unexpected client data channel error', (evt as RTCErrorEvent).error, 'error');
        this.stateMachine.emit(XrayRtcConnectionEvent.clientDataChannelError);
      };

      dataChannel.onopen = () => {
        //
        // data channel was successfully opened, so initialize the
        // RTC reporter and return data channel via callback
        //
        const {
          onDataChannelOpen,
          onReport,
          reportIntervalSeconds,
          timeoutSeconds = DEFAULT_SESSION_TIMEOUT_SECONDS,
        } = this.options;

        if (this.peerConnection) {
          this.reporter = new XrayRtcReporter({
            onReport,
            reportIntervalSeconds,
            rtcPeerConnection: this.peerConnection,
            stateMachine: this.stateMachine,
            timeoutSeconds,
          });
        }

        if (onDataChannelOpen && this.dataChannel) {
          onDataChannelOpen(this.dataChannel);
        }

        log('client data channel successfully opened');
        this.stateMachine.emit(XrayRtcConnectionEvent.clientDataChannelOpened);
      };

      this.stateMachine.emit(XrayRtcConnectionEvent.clientDataChannelCreated);
    } else {
      log(
        'unable to create data channel on peer connection',
        { isPeerConnectionCreated: !!this.peerConnection },
        'error'
      );
      this.stateMachine.emit(XrayRtcConnectionEvent.clientDataChannelError);
    }
  }

  private async initOffer() {
    try {
      const offer = await this.peerConnection?.createOffer({ iceRestart: true });
      this.stateMachine.emit(XrayRtcConnectionEvent.offerCreated);

      try {
        await this.peerConnection?.setLocalDescription(offer);
        this.stateMachine.emit(XrayRtcConnectionEvent.localDescriptionSet);

        this.socket?.send(JSON.stringify(offer));
        this.stateMachine.emit(XrayRtcConnectionEvent.offerSent);
      } catch (error) {
        log('unable to set local description', error as Error, 'error');
        this.stateMachine.emit(XrayRtcConnectionEvent.localDescriptionError);
      }
    } catch (error) {
      log('unable to create offer', error as Error, 'error');
      this.stateMachine.emit(XrayRtcConnectionEvent.offerCreateError);
    }
  }

  private initPeerConnection() {
    const peerConnection = (this.peerConnection = new RTCPeerConnection({
      iceServers: [{ urls: ICE_SERVERS }],
    }));

    peerConnection.onconnectionstatechange = () => {
      switch (this.peerConnection?.connectionState) {
        case 'closed':
          this.stateMachine.emit(XrayRtcConnectionEvent.peerConnectionClosed);
          break;
        case 'connected':
          this.stateMachine.emit(XrayRtcConnectionEvent.peerConnectionConnected);
          break;
        case 'connecting':
          this.stateMachine.emit(XrayRtcConnectionEvent.peerConnectionConnecting);
          break;
        case 'disconnected':
          this.stateMachine.emit(XrayRtcConnectionEvent.peerConnectionDisconnected);
          break;
        case 'failed':
          log('peer connection failure', undefined, 'error');
          this.stateMachine.emit(XrayRtcConnectionEvent.peerConnectionError);
          break;
      }
    };

    peerConnection.ondatachannel = (evt) => {
      evt.channel.binaryType = 'arraybuffer';
      this.stateMachine.emit(XrayRtcConnectionEvent.deviceDataChannelCreated);

      evt.channel.onclose = () => {
        this.stateMachine.emit(XrayRtcConnectionEvent.deviceDataChannelClosed);
      };

      evt.channel.onopen = () => {
        log('device data channel successfully opened');
        this.stateMachine.emit(XrayRtcConnectionEvent.deviceDataChannelOpened);
      };

      evt.channel.onerror = (evt) => {
        log('unexpected device data channel error', (evt as RTCErrorEvent).error, 'error');
        this.stateMachine.emit(XrayRtcConnectionEvent.deviceDataChannelError);
      };
    };

    peerConnection.onicecandidate = (evt: RTCPeerConnectionIceEvent) => {
      if (evt.candidate) {
        this.socket?.send(JSON.stringify(evt.candidate));
        this.stateMachine.emit(XrayRtcConnectionEvent.clientIceCandidateSent);
      }
    };

    peerConnection.onicecandidateerror = (evt) => {
      log(
        'client ice candidate gathering error',
        new Error((evt as RTCPeerConnectionIceErrorEvent).errorText),
        'warn'
      );
      this.stateMachine.emit(XrayRtcConnectionEvent.clientIceCandidateGatheringError);
    };

    peerConnection.oniceconnectionstatechange = () => {
      switch (peerConnection.iceConnectionState) {
        case 'checking':
          this.stateMachine.emit(XrayRtcConnectionEvent.iceAgentCheckingPairs);
          break;
        case 'closed':
          this.stateMachine.emit(XrayRtcConnectionEvent.iceAgentClosed);
          break;
        case 'completed':
          this.stateMachine.emit(XrayRtcConnectionEvent.iceAgentGatheringCompleted);
          break;
        case 'connected':
          this.stateMachine.emit(XrayRtcConnectionEvent.iceAgentPairsConnectedButStillChecking);
          break;
        case 'disconnected':
          this.stateMachine.emit(XrayRtcConnectionEvent.iceAgentDisconnected);
          break;
        case 'failed':
          this.stateMachine.emit(XrayRtcConnectionEvent.iceAgentFailed);
          break;
        case 'new':
          this.stateMachine.emit(XrayRtcConnectionEvent.iceAgentInitialized);
          break;
      }
    };

    peerConnection.onicegatheringstatechange = () => {
      switch (peerConnection.iceGatheringState) {
        case 'new':
          log('client ice candidate gathering has initialized');
          this.stateMachine.emit(XrayRtcConnectionEvent.clientIceCandidateGatheringInitiated);
          break;
        case 'gathering':
          log('client ice candidate gathering is in-progress');
          this.stateMachine.emit(XrayRtcConnectionEvent.clientIceCandidateGatheringInProgress);
          break;
        case 'complete':
          log('client ice candidate gathering is complete');
          this.stateMachine.emit(XrayRtcConnectionEvent.clientIceCandidateGatheringComplete);
          break;
      }
    };

    peerConnection.onsignalingstatechange = () => {
      log(`Peer connection signaling state changed to: ${peerConnection.signalingState}`);
      this.stateMachine.signalingState = peerConnection.signalingState;
    };

    this.stateMachine.emit(XrayRtcConnectionEvent.peerConnectionCreated);
  }

  private initStateMachine() {
    this.stateMachine.on(XrayRtcConnectionEvent.connectionNotSupportedError, () => {
      if (this.options.onError) {
        this.options.onError(
          'X-Ray is not supported by your current browser. Please upgrade to a newer version.'
        );
      }
    });

    this.stateMachine.on(XrayRtcConnectionEvent.connectionError, () => {
      if (this.options.onError) {
        console.error('Mason X-Ray state at time of error:', this.stateMachine.snapshot);
        console.error('Mason X-Ray signaling state at time of error:', this.stateMachine.signalingState);

        this.options.onError('X-Ray is unable to connect at this time. Please try again later.');
      }
    });
  }

  private initWebSocket(params?: URLSearchParams) {
    try {
      const { clientToken, deviceId, feature } = this.options;

      // inspect client token length to determine if this is an API key or a bearer token
      const secWebsocketProtocolAuthHeader =
        clientToken.length > MIN_BEARER_TOKEN_LENGTH
          ? `mason-bearer-token-${clientToken}`
          : `mason-apikey-${clientToken}`;

      // open a new web socket
      this.socket = new WebSocket(getSocketUrl(deviceId, feature, params), secWebsocketProtocolAuthHeader);

      this.socket.onclose = (evt: CloseEvent) => {
        if (evt.code !== 1000 && evt.code !== 1001) {
          log('web socket closed unexpectedly', evt, 'error');
          this.stateMachine.emit(XrayRtcConnectionEvent.webSocketClosedAbnormal);
        } else {
          this.stateMachine.emit(XrayRtcConnectionEvent.webSocketClosedNormal);
        }
      };

      this.socket.onerror = () => {
        log('unexpected web socket error occurred;', undefined, 'error');
        this.stateMachine.emit(XrayRtcConnectionEvent.webSocketError);

        this.socket?.close();
      };

      this.socket.onmessage = (evt: MessageEvent) => {
        if (typeof evt.data !== 'string') {
          log('binary message received', undefined, 'warn');

          // connection logic only processes string messages,
          // so early-return when non-string message is received
          return;
        }

        if (evt.data === DEVICE_ACK) {
          this.handleDeviceAck();

          // message was the device ack, so early return
          return;
        }

        //
        // message is not an ACK, so check for device-answer/device-ice-candidate
        //
        try {
          const msgData = JSON.parse(evt.data);

          if (msgData.sdp) {
            this.handleDeviceAnswer(msgData);
          } else if (msgData.candidate) {
            this.handleDeviceIceCandidate(msgData);
          } else {
            log('received unexpected socket message:', msgData, 'warn');
          }
        } catch (error) {
          log(`unable to process message: ${evt.data}`, error as Error, 'error');
          this.stateMachine.emit(XrayRtcConnectionEvent.webSocketError);
        }
      };

      this.socket.onopen = () => {
        log('web socket successfully opened');
        this.stateMachine.emit(XrayRtcConnectionEvent.webSocketOpened);
      };

      this.stateMachine.emit(XrayRtcConnectionEvent.webSocketCreated);
    } catch (error) {
      log('unexpected error during websocket initialization', error as Error, 'error');
      this.stateMachine.emit(XrayRtcConnectionEvent.webSocketError);
    }
  }

  // ---------------------------------------------------------
  //
  // message handlers
  //
  // ---------------------------------------------------------

  private handleDeviceAck() {
    //
    // let server know that we received ACK by sending back an ACK
    //
    this.stateMachine.emit(XrayRtcConnectionEvent.webSocketAckReceived);
    this.socket?.send(CLIENT_ACK);
    this.stateMachine.emit(XrayRtcConnectionEvent.webSocketAckSent);

    //
    // initialize the WebRTC workflow
    //
    this.initPeerConnection();
    this.initDataChannel();
    this.initOffer();
  }

  private async handleDeviceAnswer(answer: RTCSessionDescriptionInit) {
    try {
      await this.peerConnection?.setRemoteDescription(answer);
      this.stateMachine.emit(XrayRtcConnectionEvent.remoteDescriptionSet);
    } catch (error) {
      log('unable to set remote description', error as Error, 'error');
      this.stateMachine.emit(XrayRtcConnectionEvent.remoteDescriptionError);
    }
  }

  private async handleDeviceIceCandidate(candidate: RTCIceCandidateInit) {
    await this.peerConnection?.addIceCandidate(candidate);
    log('remote ice candidate added', { candidate }, 'debug');
    this.stateMachine.emit(XrayRtcConnectionEvent.deviceIceCandidateAdded);
  }
}

//
//
// helpers
//
//

function getSocketUrl(deviceId: string, feature: XrayRtcFeature, params?: URLSearchParams) {
  const searchString = params ? `?${params.toString()}` : '';
  let featureSegment = '';

  switch (feature) {
    case 'remote-control':
      featureSegment = 'vnc';
      break;
    case 'log-stream':
      featureSegment = 'logcat';
      break;
  }

  if (!featureSegment) {
    throw new Error(`The feature "${feature}" is not yet implemented.`);
  }

  const urlTemplate = getEndpointTemplate(`xray/:deviceId/${featureSegment}${searchString}`, 'v1', 'global');
  const url = expandUrlParams(urlTemplate, { deviceId }).replace('https', 'wss');

  return url;
}
